import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from './firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import useAuth from './useAuth';
import './App.css';

// Phase UP.0 — public user profile page at /user/:shortUid.
// Reached via the serveUserProject Cloud Function redirect from /u/:shortUid/.
//
// Phase UP.1 (2026-05-23) — owner-only inline manage actions. When the
// signed-in viewer IS the profile owner, the page shows their soft-removed
// items too AND adds per-card Unpublish ↔ Restore buttons. Same soft-delete
// shape as /account (removed: bool + removedAt: Date), so writes from either
// surface land in the same Firestore field. Also fixes a leak where
// soft-removed items previously surfaced on the PUBLIC profile because the
// pre-UP.1 filter never excluded `removed: true`.

const API_BASE = process.env.REACT_APP_API_URL || 'https://web-api-irnuiiphsq-uc.a.run.app';

async function resolveFullUid(shortUid) {
  // Resolve server-side via web-api, which runs under the Admin SDK and returns
  // only public profile fields.
  //
  // This used to be `getDocs(collection(db, 'users'))` — reading the ENTIRE
  // users collection into the browser to prefix-match. That is what forced
  // `allow read: if true` on users/{uid} in firestore.rules, which made every
  // user's email, computeTokenBalance, subscriptionTier and fcmTokens readable
  // by anyone with the project id and no credentials at all. Do not reintroduce
  // a client-side read of this collection.
  const resp = await fetch(`${API_BASE}/v1/users/by-short/${encodeURIComponent(shortUid)}`);
  // Distinguish "no such user" from "the resolver is down". Collapsing a 5xx
  // into null renders the hard "No mChatAI user matches…" page on a no-cache
  // route, which would publish that to crawlers during a bad web-api revision.
  if (resp.status === 404 || resp.status === 400) return null;
  if (!resp.ok) throw new Error(`profile resolver returned ${resp.status}`);
  const json = await resp.json();
  if (!json?.ok || !json.uid) return null;
  return { fullUid: json.uid, user: json.profile || {} };
}

async function fetchProducts(fullUid, { includeRemoved }) {
  // Public endProducts are community-visible; filter by publisherUid matching fullUid.
  // includeRemoved=true reveals soft-deleted items so the owner can Restore them;
  // includeRemoved=false (default for non-owners) keeps them out of public view.
  const q = query(collection(db, 'endProducts'), where('publisherUid', '==', fullUid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ ...d.data(), _id: d.id }))
    .filter((p) =>
      (p.visibility ?? 'public') === 'public' &&
      p.securityReviewStatus === 'approved' &&
      p.moderationStatus === 'clean' &&
      (includeRemoved || p.removed !== true)
    );
}

function productURL(product, shortUid) {
  const slug = product.slug || product.packageId || product._id;
  if (shortUid && slug) return `https://mchatai.com/u/${shortUid}/${slug}/`;
  return `/hub/${slug}`;
}

function ProductIcon({ product, size = 44 }) {
  if (product.iconURL) {
    return (
      <img
        src={product.iconURL} alt="" loading="lazy" decoding="async"
        style={{ width: size, height: size, borderRadius: 10, objectFit: 'cover', display: 'block' }}
      />
    );
  }
  const emoji = product.emoji || product.icon || '📦';
  return (
    <div style={{ fontSize: size * 0.7, lineHeight: 1, width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {emoji}
    </div>
  );
}

const pageBg = { background: '#f9fafb', minHeight: '100vh' };
const wrap = { maxWidth: 900, margin: '0 auto', padding: '48px 24px 80px' };
const card = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18,
  display: 'flex', flexDirection: 'column', gap: 10, textDecoration: 'none', color: 'inherit',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

export default function UserProfilePage() {
  const { shortUid } = useParams();
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, user: null, products: [], error: null, fullUid: null });
  const [actionError, setActionError] = useState(null);

  // Owner = viewer is signed in AND their uid matches the profile's full uid.
  // Computed against state.fullUid (resolved from the URL's shortUid) rather
  // than re-deriving shortUid from `user`, so an owner accessing via a
  // partial-uid URL still gets owner mode.
  const isOwner = !!user && !!state.fullUid && user.uid === state.fullUid;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resolved = await resolveFullUid(shortUid);
        if (cancelled) return;
        if (!resolved) {
          setState({ loading: false, user: null, products: [], error: 'notFound', fullUid: null });
          return;
        }
        if (resolved.user.publicProfileEnabled === false) {
          // Even an owner can disable their public profile. Respect it for non-owners;
          // owners still get a useful view, but only after we know who's signed in
          // (the second pass below catches that case).
          const viewerIsOwner = !!user && user.uid === resolved.fullUid;
          if (!viewerIsOwner) {
            setState({ loading: false, user: null, products: [], error: 'notFound', fullUid: null });
            return;
          }
        }
        const viewerIsOwner = !!user && user.uid === resolved.fullUid;
        const products = await fetchProducts(resolved.fullUid, { includeRemoved: viewerIsOwner });
        if (cancelled) return;
        // Sort newest-published first; within the owner view we still keep
        // hidden items inline (just visually muted) rather than relegating
        // them to a separate "hidden" section — keeps the flow simple.
        products.sort((a, b) => {
          const ta = a.publishedAt?.seconds || a.createdAt?.seconds || 0;
          const tb = b.publishedAt?.seconds || b.createdAt?.seconds || 0;
          return tb - ta;
        });
        setState({ loading: false, user: resolved.user, products, error: null, fullUid: resolved.fullUid });
      } catch (err) {
        if (!cancelled) setState({ loading: false, user: null, products: [], error: err.message, fullUid: null });
      }
    })();
    return () => { cancelled = true; };
    // user?.uid in deps so the page re-evaluates when the auth state resolves;
    // matters in the cold-load case where the profile resolves before auth.
    // Depending on the whole `user` object instead would re-run this on every
    // new identity of that object, which is not what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortUid, user?.uid]);

  async function handleRemoveToggle(item) {
    const willRemove = !item.removed;
    if (willRemove) {
      const ok = window.confirm(
        `Hide "${item.name || item.title || item.slug}" from the public Hub?\n\nThe URL keeps working — only the Hub listing hides. You can restore it from this page.`
      );
      if (!ok) return;
    }
    setActionError(null);
    try {
      await updateDoc(doc(db, 'endProducts', item._id), {
        removed: willRemove,
        removedAt: willRemove ? new Date() : null,
      });
      // Local state update so the card flips without a re-fetch.
      setState((prev) => ({
        ...prev,
        products: prev.products.map((p) =>
          p._id === item._id ? { ...p, removed: willRemove } : p
        ),
      }));
    } catch (err) {
      setActionError(err.message || (willRemove ? 'Hide failed.' : 'Restore failed.'));
    }
  }

  if (state.loading) {
    return <div style={pageBg}><div style={{ ...wrap, textAlign: 'center', color: '#6b7280' }}>Loading…</div></div>;
  }

  if (state.error === 'notFound') {
    return (
      <div style={pageBg}>
        <div style={{ ...wrap, textAlign: 'center' }}>
          <h1 style={{ color: '#111827', fontSize: '1.4rem', margin: '0 0 8px' }}>User not found</h1>
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            No mChatAI user matches <code style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>{shortUid}</code>.
          </p>
          <Link to="/hub" style={{ color: '#3b82f6', fontSize: '0.9rem' }}>Browse the Hub →</Link>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div style={pageBg}>
        <div style={{ ...wrap, textAlign: 'center' }}>
          <p style={{ color: '#6b7280' }}>Couldn't load this profile right now.</p>
        </div>
      </div>
    );
  }

  const { user: profileUser, products } = state;
  const displayName = profileUser.displayName || profileUser.profileDisplayName || 'mChatAI User';
  const bio = profileUser.profileBio || '';
  const initial = (displayName || 'U')[0].toUpperCase();
  const hiddenCount = isOwner ? products.filter((p) => p.removed === true).length : 0;

  return (
    <div style={pageBg}>
      <section style={wrap}>
        {/* Profile header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 24 }}>
          {profileUser.profileAvatarURL ? (
            <img src={profileUser.profileAvatarURL} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: '#3b82f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2rem', color: '#fff', fontWeight: 700,
            }}>{initial}</div>
          )}
          <div style={{ minWidth: 0 }}>
            <h1 style={{ color: '#111827', fontSize: '1.6rem', fontWeight: 700, margin: 0 }}>{displayName}</h1>
            {bio && <p style={{ color: '#6b7280', margin: '6px 0 0', fontSize: '0.95rem' }}>{bio}</p>}
            <p style={{ color: '#9ca3af', margin: '4px 0 0', fontSize: '0.8rem' }}>
              {products.length} public {products.length === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>

        {/* Owner-only mode banner — tells the user why their view differs from
            what visitors see, AND surfaces the "Manage on Hub" hint without a
            separate page. Keeps the "this is my management surface" mental model
            crisp. */}
        {isOwner && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
            padding: '12px 14px', marginBottom: 18, display: 'flex',
            alignItems: 'flex-start', gap: 10, fontSize: '0.85rem', color: '#1e3a8a',
          }}>
            <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>👤</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>This is your profile.</div>
              <div style={{ color: '#1e40af' }}>
                Manage published items here — visitors only see live ones.
                {hiddenCount > 0 && (
                  <> Currently hiding <strong>{hiddenCount}</strong> {hiddenCount === 1 ? 'item' : 'items'} (muted below).</>
                )}
              </div>
            </div>
          </div>
        )}

        {actionError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
            padding: '10px 14px', marginBottom: 18, fontSize: '0.85rem', color: '#b91c1c',
          }}>
            {actionError}
          </div>
        )}

        {products.length === 0 ? (
          <div style={{ ...card, alignItems: 'center', textAlign: 'center', padding: 32 }}>
            <p style={{ color: '#6b7280', margin: 0 }}>
              {isOwner
                ? "You haven't published anything to the Hub yet."
                : "This user hasn't published anything public yet."}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
            {products.map((p) => (
              <ProductCard
                key={p._id}
                product={p}
                shortUid={shortUid}
                isOwner={isOwner}
                onToggleRemove={handleRemoveToggle}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ProductCard({ product, shortUid, isOwner, onToggleRemove }) {
  const target = productURL(product, shortUid);
  const title = product.name || product.title || product.slug || '(untitled)';
  const summary = product.summary || product.description || '';
  const isRemoved = product.removed === true;

  // Owner-only soft-deleted treatment: muted card + HIDDEN label. Matches the
  // styling vocabulary on AccountPage.js so users see consistent affordances
  // across /account and /user/{shortUid}.
  const cardStyle = {
    ...card,
    opacity: isOwner && isRemoved ? 0.6 : 1,
    borderColor: isOwner && isRemoved ? '#fecaca' : '#e5e7eb',
    background: isOwner && isRemoved ? '#fef2f2' : '#fff',
  };

  return (
    <div style={cardStyle}>
      {/* Anchor wraps just the icon + title + summary so the owner action row
          underneath doesn't double-trigger navigation. Same trick AccountPage
          uses — buttons live OUTSIDE the link element rather than fighting
          click bubbling. */}
      <a href={target} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ProductIcon product={product} size={40} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title}
              {isOwner && isRemoved && (
                <span style={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 700, marginLeft: 6 }}>HIDDEN</span>
              )}
            </div>
            {product.installCount > 0 && (
              <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>Installed {product.installCount}×</div>
            )}
          </div>
        </div>
        {summary && (
          <div style={{ color: '#6b7280', fontSize: '0.82rem', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {summary}
          </div>
        )}
      </a>

      {isOwner && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
          <button
            onClick={(e) => { e.preventDefault(); onToggleRemove(product); }}
            title={isRemoved ? 'Restore to the Hub' : 'Hide from the Hub (URL still works)'}
            style={{
              background: isRemoved ? '#16a34a' : 'transparent',
              color: isRemoved ? '#fff' : '#b91c1c',
              border: `1px solid ${isRemoved ? '#16a34a' : '#fecaca'}`,
              padding: '4px 12px', borderRadius: 6, cursor: 'pointer',
              fontSize: '0.75rem', fontWeight: 600,
              minHeight: 28,
            }}
          >
            {isRemoved ? 'Restore' : 'Unpublish'}
          </button>
        </div>
      )}
    </div>
  );
}
